<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Exit Settings</h5>
        <p class="page-sub">Configure default notice periods, clearance and automation</p>
      </div>
    </div>

    <div v-if="loading" class="sk-table" style="max-width:680px;margin:0 auto;">
      <div v-for="n in 5" :key="n" class="sk-row"></div>
    </div>

    <div v-else class="form-card" style="max-width:680px;margin:0 auto;">

      <div class="section-label">Default Configuration</div>

      <div class="form-row">
        <label class="form-label">Default Notice Period (days)</label>
        <input v-model.number="form.default_notice_days" type="number" min="0" max="365" class="form-input" style="max-width:160px;" />
        <span class="form-hint">Used when no explicit notice period is specified during case creation.</span>
      </div>

      <hr class="section-divider" />

      <div class="section-label">Workflow</div>

      <div class="setting-row">
        <div class="toggle-info">
          <span class="toggle-name">Require Clearance</span>
          <span class="toggle-hint">Generate clearance tasks when a case is approved</span>
        </div>
        <label class="toggle-switch">
          <input type="checkbox" v-model="form.require_clearance" />
          <span class="toggle-track"></span>
        </label>
      </div>

      <div class="setting-row" style="margin-top:14px;">
        <div class="toggle-info">
          <span class="toggle-name">Require Exit Interview</span>
          <span class="toggle-hint">Block case completion until an exit interview is conducted</span>
        </div>
        <label class="toggle-switch">
          <input type="checkbox" v-model="form.require_exit_interview" />
          <span class="toggle-track"></span>
        </label>
      </div>

      <div class="setting-row" style="margin-top:14px;">
        <div class="toggle-info">
          <span class="toggle-name">Auto-Revoke Access</span>
          <span class="toggle-hint">Automatically deactivate employee account on last working day</span>
        </div>
        <label class="toggle-switch">
          <input type="checkbox" v-model="form.auto_revoke_access" />
          <span class="toggle-track"></span>
        </label>
      </div>

      <hr class="section-divider" />

      <div class="section-label">Clearance Department Template</div>
      <p class="form-hint" style="margin-bottom:14px;">These departments and tasks are auto-generated when clearance starts for each exit case.</p>

      <div v-for="(dept, idx) in form.clearance_departments" :key="idx" class="dept-row">
        <input v-model="dept.department" class="form-input dept-input" placeholder="Department" />
        <input v-model="dept.task" class="form-input task-input" placeholder="Task description" />
        <button class="btn-del" @click="removeDept(idx)">✕</button>
      </div>

      <button class="btn-add-dept" @click="addDept">+ Add Department</button>

      <div style="display:flex;justify-content:flex-end;margin-top:24px;">
        <button class="btn-primary-action" @click="save" :disabled="saving">
          <font-awesome-icon v-if="saving" icon="spinner" spin />
          {{ saving ? 'Saving…' : 'Save Settings' }}
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'

export default {
  name: 'ExitSettings',
  setup() { return { toast: useToast() } },
  data() {
    return {
      loading: true,
      saving: false,
      form: {
        default_notice_days:    30,
        require_clearance:      true,
        require_exit_interview: true,
        auto_revoke_access:     true,
        clearance_departments:  [],
      },
    }
  },
  created() { this.load() },
  methods: {
    async load() {
      try {
        const r = await axios.get('exits/settings')
        const s = r.data.data
        this.form = {
          default_notice_days:    s.default_notice_days ?? 30,
          require_clearance:      s.require_clearance,
          require_exit_interview: s.require_exit_interview,
          auto_revoke_access:     s.auto_revoke_access,
          clearance_departments:  s.clearance_departments ?? [],
        }
      } catch { this.toast.error('Failed to load settings.') }
      finally { this.loading = false }
    },
    async save() {
      this.saving = true
      try {
        await axios.put('exits/settings', this.form)
        this.toast.success('Settings saved.')
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to save.')
      } finally { this.saving = false }
    },
    addDept() {
      this.form.clearance_departments.push({ department: '', task: '' })
    },
    removeDept(idx) {
      this.form.clearance_departments.splice(idx, 1)
    },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }

.form-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:24px; }
.form-row  { display:flex; flex-direction:column; gap:5px; }
.form-label { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; font-family:inherit; }
.form-input:focus { border-color:#004AAD; }
.form-hint  { font-size:11px; color:#aab8cc; margin-top:2px; }

.section-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; margin-bottom:14px; }
.section-divider { border:none; border-top:1px solid #f0f4f8; margin:20px 0; }

.setting-row { display:flex; justify-content:space-between; align-items:center; gap:16px; padding:4px 0; }
.toggle-info { display:flex; flex-direction:column; gap:3px; }
.toggle-name { font-size:13px; font-weight:600; color:#1a2b45; }
.toggle-hint { font-size:12px; color:#7a90b0; }
.toggle-switch { position:relative; display:inline-block; width:38px; height:22px; flex-shrink:0; }
.toggle-switch input { opacity:0; width:0; height:0; }
.toggle-track  { position:absolute; cursor:pointer; inset:0; background:#dde5f0; border-radius:22px; transition:.2s; }
.toggle-track::before { position:absolute; content:''; height:16px; width:16px; left:3px; top:3px; background:#fff; border-radius:50%; transition:.2s; }
.toggle-switch input:checked + .toggle-track { background:#004AAD; }
.toggle-switch input:checked + .toggle-track::before { transform:translateX(16px); }

.dept-row { display:flex; gap:10px; align-items:center; margin-bottom:10px; }
.dept-input { flex:0 0 160px; }
.task-input { flex:1; }
.btn-del { background:none; border:none; color:#ef4444; cursor:pointer; font-size:16px; padding:4px 8px; flex-shrink:0; }

.btn-add-dept { margin-top:4px; background:none; border:1.5px dashed #dde5f0; color:#7a90b0; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; cursor:pointer; font-family:inherit; width:100%; }
.btn-add-dept:hover { border-color:#004AAD; color:#004AAD; }

.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row   { height:52px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-bottom:1px solid #e4eaf2; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
</style>
